/* eslint-disable jsx-a11y/control-has-associated-label */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ISpirometryTestResult } from "../../../../../../@types/Measurements";
import { roundToX } from "../../../../../../utils/unitOperations";
import { StyledTable, TableContainer } from "./SpirometryValuesSection.styles";

type SpirometryValuesSectionProps = {
  spirometryTests: ISpirometryTestResult[];
  archivedSpirometryTestsIds: number[];
};

export type SpirometryMaxValues = {
  fvc: number | undefined;
  fev1: number | undefined;
  fev1_fvc: number | undefined;
  pef: number | undefined;

  calculated_predicted_fev1: number | undefined;
  calculated_predicted_fvc: number | undefined;
};

export function SpirometryValuesSection({
  spirometryTests,
  archivedSpirometryTestsIds,
}: SpirometryValuesSectionProps) {
  const { t } = useTranslation();

  const [maxValues, setMaxValues] = useState<SpirometryMaxValues>();

  const getMaxValues = () => {
    const archivedSet = new Set(archivedSpirometryTestsIds);

    return spirometryTests?.reduce<SpirometryMaxValues>(
      (acc, t) => {
        if (archivedSet.has(t.spirometry_test_id)) return acc;

        if (Number.isFinite(t.fvc)) {
          if (acc.fvc === undefined || t.fvc > acc.fvc) {
            acc.fvc = t.fvc;
            acc.calculated_predicted_fvc = t.calculated_predicted_fvc;
          }
        }

        if (Number.isFinite(t.fev1)) {
          if (acc.fev1 === undefined || t.fev1 > acc.fev1) {
            acc.fev1 = t.fev1;
            acc.calculated_predicted_fev1 = t.calculated_predicted_fev1;
          }
        }

        if (Number.isFinite(t.fev1_fvc)) {
          acc.fev1_fvc =
            acc.fev1_fvc === undefined
              ? t.fev1_fvc
              : Math.max(acc.fev1_fvc, t.fev1_fvc);
        }

        if (Number.isFinite(t.pef)) {
          acc.pef = acc.pef === undefined ? t.pef : Math.max(acc.pef, t.pef);
        }

        return acc;
      },
      {
        fvc: undefined,
        fev1: undefined,
        fev1_fvc: undefined,
        pef: undefined,
        calculated_predicted_fev1: undefined,
        calculated_predicted_fvc: undefined,
      }
    );
  };

  const getPercentPredicted = (value: string, predicted: string) => {
    return roundToX(
      (Number.parseFloat(value) / Number.parseFloat(predicted)) * 100,
      2
    );
  };

  useEffect(() => {
    const maxValues = getMaxValues();

    setMaxValues(maxValues);
  }, [spirometryTests, archivedSpirometryTestsIds]);

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th />
            <th>{t("patientPage_measurementModal_best")}</th>
            <th>{t("patientPage_measurementModal_percentPredicted")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>FVC (L)</td>
            <td>{maxValues?.fvc ?? "-"}</td>
            <td>
              {maxValues?.calculated_predicted_fvc && maxValues?.fvc
                ? getPercentPredicted(
                    maxValues.fvc.toString(),
                    maxValues.calculated_predicted_fvc.toString()
                  )
                : "-"}
            </td>
          </tr>
          <tr>
            <td>FEV1 (L)</td>
            <td>{maxValues?.fev1 ?? "-"}</td>
            <td>
              {maxValues?.calculated_predicted_fev1 && maxValues?.fev1
                ? getPercentPredicted(
                    maxValues.fev1.toString(),
                    maxValues.calculated_predicted_fev1.toString()
                  )
                : "-"}
            </td>
          </tr>
          <tr>
            <td>FEV1/FVC (%)</td>
            <td>
              {maxValues?.fev1_fvc !== undefined
                ? `${roundToX(maxValues.fev1_fvc * 100, 0)}%`
                : "-"}
            </td>
            <td />
          </tr>
          <tr>
            <td>PEF (L/s)</td>
            <td>{maxValues?.pef ?? "-"}</td>
            <td />
          </tr>
        </tbody>
      </StyledTable>
    </TableContainer>
  );
}
